/**
 * BPTargetsPanel — the Cibles detail of one axis (or of the three, `axis: 'all'`):
 * the `…/dashboard/targets` fetch + the by-id label join + `BPTargetsView`, plus
 * `BPContentsCard` for contenu.
 *
 * Lifted out of `BrandAxisDashboard` (2026-10-14) when its Contenu / Produits sub-tabs
 * moved to the « Cibles citées » card of the Vue d'ensemble — the fetch, the label
 * posture and the stale-response guard are unchanged, only their owner is.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton, Typography } from '@mui/material';
import type { BPTargetsAxis, BPTargetsResponse, TargetCategory, TargetsManifest } from '@traaker/shared';
import { api } from '../../services/api';
import { CARD } from '../viz';
import { resolveTargetLabels, type TargetLabel } from './targetLabels';
import BPTargetsView from './BPTargetsView';
import BPContentsCard from './BPContentsCard';

/**
 * Entries whose label + url are resolved for the leaderboards. `TABLE_ROWS` in
 * `BPTargetsView` is 100, so this covers every row either card can show, and it sits
 * under `TARGET_LIMITS.maxTargetsByIdLookup` (200) which the service enforces anyway.
 */
const TARGET_LABEL_LOOKUP_MAX = 100;

export interface BPTargetsPanelProps {
  brandId: string;
  axis: BPTargetsAxis;
  kind: 'contenu' | 'produit' | 'personne';
  /** The ONE category requested — `produit` or `collection` on the Produits side. */
  category: TargetCategory;
  manifest: TargetsManifest;
  /** The window AFTER `resolveBpRange` — inclusive days. */
  from: string;
  to: string;
  spanClamped: boolean;
  /** Cibles tag IDS from the manifest registry. */
  targetTags: number[];
}

export default function BPTargetsPanel({
  brandId,
  axis,
  kind,
  category,
  manifest,
  from,
  to,
  spanClamped,
  targetTags,
}: BPTargetsPanelProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<BPTargetsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  /**
   * `targetId → {label, url}` for the rows the leaderboards render. `BPTopEntry.l` /
   * `.u` are NEVER written (the fold cannot reach a catalogue field), so without this
   * join the cards can only show a 6-char id and nothing is clickable. Resolved at
   * READ time from the home rows: a rename must not need a re-fold (D6b).
   */
  const [labels, setLabels] = useState<Map<string, TargetLabel>>(new Map());

  useEffect(() => {
    const params = new URLSearchParams({ axis, from, to, category });
    if (targetTags.length) params.set('targetTags', targetTags.join(','));

    // Synchronously, NOT inside the debounce: for the 300 ms before the request leaves,
    // `data` still holds the previous scope's response, which would be a false claim
    // under this one's headings.
    setLoading(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .get<BPTargetsResponse>(`/brands/${brandId}/brand-perception/dashboard/targets?${params}`)
        .then((resp) => {
          if (cancelled) return;
          setData(resp ?? null);
          setLoadError('');
          // Bounded by construction: only the entries the card can render. The session
          // cache asks the network for ids never seen before, so a scope change costs
          // nothing once the top ids are known. `ct` is the category hint: one
          // candidate shard per id instead of one per owner category.
          const ids = (resp?.top ?? [])
            .slice(0, TARGET_LABEL_LOOKUP_MAX)
            .map((e) => ({ targetId: e.id, category: e.ct }));
          if (ids.length === 0) {
            setLabels(new Map());
            return;
          }
          resolveTargetLabels(brandId, ids).then((map) => {
            // Replaced, not merged: a different row set, and a label left over from the
            // previous one would be a wrong name on a recycled row.
            if (!cancelled) setLabels(map);
          });
        })
        .catch((err: any) => {
          if (cancelled) return;
          setData(null);
          setLoadError(err?.message || t('bpDash.loadError'));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, axis, category, from, to, targetTags]);

  if (loading) return <Skeleton variant="rounded" height={320} />;
  if (loadError || !data) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {loadError || t('bpDash.noData')}
        </Typography>
      </Box>
    );
  }

  const view = (
    <BPTargetsView kind={kind} data={data} manifest={manifest} labels={labels} spanClamped={spanClamped} />
  );
  if (kind !== 'contenu') return view;
  // « Contenus cités » (docs/features/brand-perception.md §9.13): its own endpoint and grain.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${CARD.gap * 8}px` }}>
      {view}
      {/* Keyed: a brand/axis switch remounts with empty state, so brand A's table never
          resolves its linked-product ids against brand B. */}
      <BPContentsCard
        key={`${brandId}:${axis}`}
        brandId={brandId}
        axis={axis}
        from={from}
        to={to}
        spanClamped={spanClamped}
        tagFilterActive={targetTags.length > 0}
      />
    </Box>
  );
}
